import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { mlbAPI } from '../services/apiService';

// DD-FIXES-0705 Fix 1: per-player lazy fetch.
//
// The previous provider fetched the UNFILTERED notes feed once (the backend's
// most-recent-500 rows league-wide) and bucketed it by player_id — so any
// player without league-wide-recent news rendered zero notes even with a full
// season stored (Bug 1, BUG_1_PLAYER_NOTES_INVESTIGATION_20260705.md §3).
// The global feed is NEVER the data source for a single-player view. Each
// player's notes are fetched per-player via mlbAPI.getPlayerNotes(mlbId)
// (the same backend branch the mobile News tab uses) and cached per id for
// the provider's lifetime.

const PlayerNotesContext = createContext(null);

export const PlayerNotesProvider = ({ children }) => {
  // notesByPlayer[mlbId]: undefined = never fetched; [] = fetched, none/error
  const [notesByPlayer, setNotesByPlayer] = useState({});
  const pendingRef = useRef(new Set());

  const fetchNotesForPlayer = useCallback(async (mlbId) => {
    if (!mlbId || pendingRef.current.has(mlbId)) return;
    pendingRef.current.add(mlbId);
    try {
      const notes = await mlbAPI.getPlayerNotes(mlbId);
      setNotesByPlayer((prev) => ({ ...prev, [mlbId]: Array.isArray(notes) ? notes : [] }));
    } catch (err) {
      // Surface the failure (no silent data-source substitution); render the
      // honest empty state rather than stale or foreign data.
      console.error(`Player notes fetch failed for ${mlbId}:`, err);
      setNotesByPlayer((prev) => ({ ...prev, [mlbId]: [] }));
    } finally {
      pendingRef.current.delete(mlbId);
    }
  }, []);

  return (
    <PlayerNotesContext.Provider value={{ notesByPlayer, fetchNotesForPlayer }}>
      {children}
    </PlayerNotesContext.Provider>
  );
};

export const usePlayerNotes = (mlbId) => {
  const ctx = useContext(PlayerNotesContext);
  const notesByPlayer = ctx?.notesByPlayer;
  const fetchNotesForPlayer = ctx?.fetchNotesForPlayer;

  useEffect(() => {
    if (mlbId && fetchNotesForPlayer && notesByPlayer && notesByPlayer[mlbId] === undefined) {
      fetchNotesForPlayer(mlbId);
    }
  }, [mlbId, notesByPlayer, fetchNotesForPlayer]);

  if (!mlbId || !notesByPlayer || !notesByPlayer[mlbId]) return [];
  return notesByPlayer[mlbId];
};
